import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { PuzzleTopology, PuzzleAnswer } from '@/types/puzzle'

interface PuzzleContextType {
  topology: PuzzleTopology
  values: PuzzleAnswer
  setValues: (values: PuzzleAnswer) => void
  setValue: (nodeId: string, value: number | string) => void
  lineStatus: Record<string, { sum: number, isFull: boolean, isValid: boolean }>
  focusedNodeId: string | null
  setFocusedNodeId: (id: string | null) => void
}

const PuzzleContext = createContext<PuzzleContextType | undefined>(undefined)

export const usePuzzle = () => {
  const context = useContext(PuzzleContext)
  if (!context) {
    throw new Error('usePuzzle must be used within a PuzzleProvider')
  }
  return context
}

interface PuzzleProviderProps {
  topology: PuzzleTopology
  initialValues?: PuzzleAnswer
  onChange?: (values: PuzzleAnswer) => void
  children: React.ReactNode
}

export const PuzzleProvider: React.FC<PuzzleProviderProps> = ({ 
  topology, 
  initialValues = {}, 
  onChange,
  children 
}) => {
  const [values, setValuesState] = useState<PuzzleAnswer>(initialValues)
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null)
  
  // Calculate line status
  const lineStatus = React.useMemo(() => {
    const status: Record<string, { sum: number, isFull: boolean, isValid: boolean }> = {}
    
    topology.lines.forEach(line => {
      let sum = 0
      let isFull = true
      
      for (const nodeId of line.nodeIds) {
        const val = values[nodeId]
        if (val === undefined || val === '') {
          isFull = false
        } else {
          // Try to parse as number if possible
          const num = typeof val === 'string' ? parseFloat(val) : val
          if (!isNaN(num)) {
            sum += num
          }
        }
      }
      
      const isValid = line.targetSum !== undefined ? sum === line.targetSum : true
      
      status[line.id] = { sum, isFull, isValid }
    })
    
    return status
  }, [topology, values])

  const setValues = useCallback((newValues: PuzzleAnswer) => {
    setValuesState(newValues)
    onChange?.(newValues)
  }, [onChange])

  const setValue = useCallback((nodeId: string, value: number | string) => {
    setValuesState(prev => {
      const next = { ...prev, [nodeId]: value }
      onChange?.(next)
      return next
    })
  }, [onChange])

  // Initialize locked values
  useEffect(() => {
    const lockedValues: PuzzleAnswer = {}
    let hasLocked = false
    
    topology.nodes.forEach(node => {
      if (node.isLocked && node.initialValue !== undefined) {
        lockedValues[node.id] = node.initialValue
        hasLocked = true
      }
    })
    
    if (hasLocked) {
      setValuesState(prev => ({ ...prev, ...lockedValues }))
    }
  }, [topology])

  return (
    <PuzzleContext.Provider value={{ 
      topology, 
      values, 
      setValues, 
      setValue, 
      lineStatus,
      focusedNodeId,
      setFocusedNodeId
    }}>
      {children}
    </PuzzleContext.Provider>
  )
}
